import { useEffect, useState } from 'react';
import { formatCurrency } from '../../lib/format';
import {
  fetchBehandelingCatalog,
  addKleedBehandeling,
  type BehandelingCatalog,
  type BehandelingCatalogItem,
} from '../../lib/kleed-behandeling';

interface KleedBehandelingDialogProps {
  isOpen: boolean;
  parentLeadProductId: string | null;
  parentName?: string;
  onClose: () => void;
  onAdded: () => void;
}

export function KleedBehandelingDialog({
  isOpen,
  parentLeadProductId,
  parentName,
  onClose,
  onAdded,
}: KleedBehandelingDialogProps) {
  const [catalog, setCatalog] = useState<BehandelingCatalog | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !parentLeadProductId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCatalog(null);
    fetchBehandelingCatalog(parentLeadProductId)
      .then((data) => {
        if (cancelled) return;
        setCatalog(data);
        if (!data.success && data.message) setError(data.message);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Catalog laden mislukt');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, parentLeadProductId]);

  if (!isOpen || !parentLeadProductId) return null;

  const availableItems = (catalog?.items ?? []).filter((i) => !i.alreadyLinked);

  async function handleAdd(item: BehandelingCatalogItem) {
    if (!parentLeadProductId) return;
    setBusyCode(item.sku);
    setError(null);
    try {
      const res = await addKleedBehandeling({
        parentLeadProductId,
        behandelingCode: item.sku,
      });
      if (!res.success) {
        setError(res.message || 'Toevoegen mislukt');
        return;
      }
      onAdded();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setBusyCode(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Behandeling toevoegen</h3>
            {(catalog?.parentName || parentName) && (
              <p className="text-xs text-slate-500 mt-0.5">
                Voor: {catalog?.parentName || parentName}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mb-3">
              {error}
            </div>
          )}

          {!loading && catalog?.success && availableItems.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-6">
              Alle behandelingen zijn al gekoppeld.
            </p>
          )}

          {!loading && availableItems.length > 0 && (
            <ul className="space-y-2">
              {availableItems.map((item) => (
                <li
                  key={item.sku}
                  className="flex items-start gap-3 border border-slate-200 rounded-lg p-3 hover:border-indigo-300"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{item.name}</div>
                    {item.description && (
                      <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                    )}
                    {typeof item.unitPriceInclVat === 'number' && item.unitPriceInclVat > 0 && (
                      <p className="text-xs text-slate-600 mt-1">
                        + {formatCurrency(item.unitPriceInclVat)} incl. btw
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={busyCode !== null}
                    onClick={() => handleAdd(item)}
                    className="text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-slate-300 flex-shrink-0"
                  >
                    {busyCode === item.sku ? 'Bezig…' : 'Toevoegen'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}
